import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  AddIcon,
  DeleteIcon,
  DownloadIcon,
  DragAndDropIcon,
  EditIcon,
  InfoIcon,
  LockIcon,
  PreviewIcon,
  ShowNotesIcon,
  TranscribeIcon,
  VideoIcon,
} from 'src/v3/core/icons'
import {
  TextContent,
  ContentComplianceStatus,
  CourseMigratableVideo,
  CourseMigratableShowNote,
  StreamStep,
} from 'src/v3/core/types'
import { modalInputSx, modalPaperProps } from 'src/v3/utilities/modal-styles'
import { uploadVideoToS3 } from 'src/v3/create/v5/utils/upload'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import axios from 'src/utilities/http-client'
import {
  createCourseShowNotes,
  createCourseVideo,
  deleteCourseShowNotes,
  deleteCourseVideo,
  fetchCourseV2LegacyTextContent,
  fetchCourseV2ShowNotes,
  fetchCourseV2Videos,
  migrateCourseV2Video,
  migrateCourseV2ShowNote,
  updateCourseShowNotes,
  updateCourseVideo,
  updateCourseV2ContentSteps,
  selectCourseV2LegacyTextContentByCourseID,
  selectCourseV2LegacyTextContentStatusByCourseID,
  selectCourseV2ShowNotesByCourseID,
  selectCourseV2ShowNotesStatusByCourseID,
  selectCourseV2VideosByCourseID,
  selectCourseV2VideosStatusByCourseID,
  selectDeleteCourseShowNotesError,
  selectDeleteCourseShowNotesStatus,
  selectDeleteCourseVideoError,
  selectDeleteCourseVideoStatus,
} from 'src/v3/content-pipeline/course-pipeline.slice'
import {
  PipelineShowNotes,
  PipelineVideo,
} from 'src/v3/content-pipeline/utils/course-api'
import {
  CourseContentItem,
  orderCourseContent,
} from 'src/v3/content-pipeline/utils/order-course-content'
import MigrateTextContentModal from './migrate-text-content-modal'
import CourseShowNotesModal from './course-show-notes-modal'
import CourseShowNotesPreviewModal from './course-show-notes-preview-modal'
import PipelineTranscriptEditorModal from './pipeline-transcript-editor-modal'
import VideoPreviewModal from './video-preview-modal'
import DownloadVideoModal from './download-video-modal'

type Props = {
  courseID: string
  courseSteps: StreamStep[]
  complianceStatus?: ContentComplianceStatus
  complianceFeedback?: string
  migratableVideos?: CourseMigratableVideo[]
  migratableShowNotes?: CourseMigratableShowNote[]
}

type UnifiedItem =
  | CourseContentItem
  | { kind: 'legacy'; data: TextContent }
  | { kind: 'migratableVideo'; data: CourseMigratableVideo }
  | { kind: 'migratableShowNote'; data: CourseMigratableShowNote }

let complianceChipColor = (
  status: ContentComplianceStatus,
): 'default' | 'primary' | 'success' | 'error' => {
  if (status === 'approved') return 'success'
  if (status === 'rejected' || status === 'unapproved') return 'error'
  return 'primary'
}

let complianceChipLabel = (status: ContentComplianceStatus): string => {
  if (status === 'new') return 'Pending Review'
  if (status === 'updated') return 'Updated – Pending Review'
  if (status === 'approved') return 'Approved'
  if (status === 'rejected') return 'Rejected'
  if (status === 'unapproved') return 'Unapproved'
  return status
}

export default function CourseContentTab({
  courseID,
  courseSteps,
  complianceStatus,
  complianceFeedback,
  migratableVideos = [],
  migratableShowNotes = [],
}: Props): ReactElement {
  let dispatch = useAppDispatch()
  let { showError, showSuccess, NotificationComponent } = useNotifications()

  let showNotesList = useAppSelector(
    selectCourseV2ShowNotesByCourseID(courseID),
  )
  let showNotesStatus = useAppSelector(
    selectCourseV2ShowNotesStatusByCourseID(courseID),
  )
  let videosList = useAppSelector(selectCourseV2VideosByCourseID(courseID))
  let videosStatus = useAppSelector(
    selectCourseV2VideosStatusByCourseID(courseID),
  )
  let legacyTextContent = useAppSelector(
    selectCourseV2LegacyTextContentByCourseID(courseID),
  )
  let legacyTextContentStatus = useAppSelector(
    selectCourseV2LegacyTextContentStatusByCourseID(courseID),
  )

  useEffect(() => {
    if (!showNotesStatus || showNotesStatus === 'uninitialized') {
      dispatch(fetchCourseV2ShowNotes(courseID))
    }
  }, [showNotesStatus, courseID, dispatch])

  useEffect(() => {
    if (!videosStatus || videosStatus === 'uninitialized') {
      dispatch(fetchCourseV2Videos(courseID))
    }
  }, [videosStatus, courseID, dispatch])

  useEffect(() => {
    if (
      !legacyTextContentStatus ||
      legacyTextContentStatus === 'uninitialized'
    ) {
      dispatch(fetchCourseV2LegacyTextContent(courseID))
    }
  }, [legacyTextContentStatus, courseID, dispatch])

  // ── Local ordering state ──────────────────────────────────────────────────

  let [localUnifiedContent, setLocalUnifiedContent] = useState<UnifiedItem[]>(
    [],
  )

  useEffect(() => {
    let {
      items: combined,
      hasUnsteppedItems,
      hasStaleSteps,
    } = orderCourseContent(courseSteps, showNotesList, videosList)
    setLocalUnifiedContent(combined)

    // Newly added (or migrated) content has no StreamStep row yet, and
    // deleted content may leave a stale one behind — persist the full,
    // reconciled list immediately so `content.steps` doesn't silently drift
    // until the planner happens to drag-and-drop something.
    if (hasUnsteppedItems || hasStaleSteps) {
      let steps = combined.map((i) => ({
        type: (i.kind === 'video' ? 'Video' : 'ShowNotes') as
          'Video' | 'ShowNotes',
        videoID: i.kind === 'video' ? i.data.id : null,
        showNotesID: i.kind === 'showNotes' ? i.data.id : null,
      }))
      dispatch(updateCourseV2ContentSteps({ courseId: courseID, steps }))
    }
  }, [courseSteps, showNotesList, videosList, courseID, dispatch])

  // ── Modal / dialog state ──────────────────────────────────────────────────

  let [migratingTextContent, setMigratingTextContent] =
    useState<TextContent | null>(null)
  let [editingShowNotesID, setEditingShowNotesID] = useState<string | null>(
    null,
  )
  let [previewingShowNotes, setPreviewingShowNotes] =
    useState<PipelineShowNotes | null>(null)
  let [deletingShowNotesID, setDeletingShowNotesID] = useState<string | null>(
    null,
  )
  let [deletingVideoID, setDeletingVideoID] = useState<string | null>(null)
  let deleteShowNotesStatus = useAppSelector(selectDeleteCourseShowNotesStatus)
  let deleteShowNotesError = useAppSelector(selectDeleteCourseShowNotesError)
  let deleteVideoStatus = useAppSelector(selectDeleteCourseVideoStatus)
  let deleteVideoError = useAppSelector(selectDeleteCourseVideoError)
  let isDeletingShowNotes = deleteShowNotesStatus === 'pending'
  let isDeletingVideo = deleteVideoStatus === 'pending'
  let [previewingVideo, setPreviewingVideo] = useState<PipelineVideo | null>(
    null,
  )
  let [downloadingVideo, setDownloadingVideo] = useState<PipelineVideo | null>(
    null,
  )
  let [editingShowNotesInfo, setEditingShowNotesInfo] =
    useState<PipelineShowNotes | null>(null)
  let [editSnInfoName, setEditSnInfoName] = useState('')
  let [editSnInfoDescription, setEditSnInfoDescription] = useState('')
  let [editingVideoInfo, setEditingVideoInfo] = useState<PipelineVideo | null>(
    null,
  )
  let [editVideoInfoName, setEditVideoInfoName] = useState('')
  let [editVideoInfoDescription, setEditVideoInfoDescription] = useState('')

  // ── Transcript state ──────────────────────────────────────────────────────

  let [transcriptAvailability, setTranscriptAvailability] = useState<
    Record<string, boolean>
  >({})
  let [transcribing, setTranscribing] = useState<Record<string, boolean>>({})
  let [transcriptModalVideoId, setTranscriptModalVideoId] = useState<
    string | null
  >(null)
  let [transcribeConfirmVideoId, setTranscribeConfirmVideoId] = useState<
    string | null
  >(null)

  // ── Add show notes dialog ─────────────────────────────────────────────────

  let transcribePollIntervalsRef = useRef<Set<ReturnType<typeof setInterval>>>(
    new Set(),
  )

  useEffect(() => {
    let intervals = transcribePollIntervalsRef.current
    return () => intervals.forEach(clearInterval)
  }, [])

  let expectedSnCountRef = useRef<number | null>(null)
  let [addSnFormOpen, setAddSnFormOpen] = useState(false)
  let [addSnName, setAddSnName] = useState('')
  let [addSnDescription, setAddSnDescription] = useState('')

  // ── Add video dialog ──────────────────────────────────────────────────────

  let [addVideoOpen, setAddVideoOpen] = useState(false)
  let [videoName, setVideoName] = useState('')
  let [videoDescription, setVideoDescription] = useState('')
  let [videoFile, setVideoFile] = useState<File | null>(null)
  let [videoUploadProgress, setVideoUploadProgress] = useState<number | null>(
    null,
  )
  let [videoUploadError, setVideoUploadError] = useState<string | null>(null)

  // ── Approved-field confirmation ───────────────────────────────────────────

  let [approvedConfirmOpen, setApprovedConfirmOpen] = useState(false)
  let [pendingAddKind, setPendingAddKind] = useState<
    'showNotes' | 'video' | null
  >(null)

  let unmigratableVideos = migratableVideos.filter((v) => !v.isMigrated)
  let unmigratableShowNotes = migratableShowNotes.filter((sn) => !sn.isMigrated)

  // Migratable items are kept separate so they render even while the
  // active-course content (videos / show notes) is still loading.
  let migratableItems: UnifiedItem[] = [
    ...unmigratableVideos.map((v): UnifiedItem => ({
      kind: 'migratableVideo',
      data: v,
    })),
    ...unmigratableShowNotes.map((sn): UnifiedItem => ({
      kind: 'migratableShowNote',
      data: sn,
    })),
  ]

  let unifiedList: UnifiedItem[] = [
    ...localUnifiedContent,
    ...legacyTextContent.map((tc): UnifiedItem => ({
      kind: 'legacy',
      data: tc,
    })),
  ]

  // ── Transcript availability check ────────────────────────────────────────

  useEffect(() => {
    if (videosStatus !== 'fulfilled' || videosList.length === 0) return

    let checkTranscripts = async () => {
      let results = await Promise.all(
        videosList.map(async (video) => {
          try {
            let response = await axios.get(
              `/api/pipeline/planning/courses/videos/${video.id}/transcript`,
            )
            if (response.data) {
              try {
                await axios.get(response.data)
                return { id: video.id, hasTranscript: true }
              } catch {
                return { id: video.id, hasTranscript: false }
              }
            }
            return { id: video.id, hasTranscript: false }
          } catch {
            return { id: video.id, hasTranscript: false }
          }
        }),
      )
      setTranscriptAvailability(
        results.reduce(
          (acc, { id, hasTranscript }) => ({ ...acc, [id]: hasTranscript }),
          {} as Record<string, boolean>,
        ),
      )
    }

    checkTranscripts()
  }, [videosStatus, videosList])

  let handleTranscribe = async (videoId: string) => {
    setTranscribing((prev) => ({ ...prev, [videoId]: true }))

    let finishTranscribing = (outcome: 'success' | 'timeout' | 'error') => {
      setTranscribing((prev) => ({ ...prev, [videoId]: false }))
      if (outcome === 'success') {
        setTranscriptAvailability((prev) => ({ ...prev, [videoId]: true }))
        showSuccess('Transcript is ready to edit.')
      } else if (outcome === 'timeout') {
        showError(
          'Transcription is taking longer than expected. It may still finish in the background — check back in a few minutes, or try again.',
        )
      } else {
        showError('Failed to transcribe this video. Please try again.')
      }
    }

    try {
      await axios.post(
        `/api/pipeline/planning/courses/videos/${videoId}/transcript/trigger`,
      )

      let pollCount = 0
      let maxPollCount = 60 // 10 minutes at 10-second intervals

      let stopPolling = (interval: ReturnType<typeof setInterval>) => {
        clearInterval(interval)
        transcribePollIntervalsRef.current.delete(interval)
      }

      let pollInterval = setInterval(async () => {
        try {
          // eslint-disable-next-line fp/no-mutation
          pollCount += 1
          let response = await axios.get(
            `/api/pipeline/planning/courses/videos/${videoId}/transcript`,
          )

          if (response.status === 200 && response.data) {
            try {
              await axios.get(response.data)
              finishTranscribing('success')
              stopPolling(pollInterval)
            } catch {
              if (pollCount >= maxPollCount) {
                finishTranscribing('timeout')
                stopPolling(pollInterval)
              }
            }
          } else if (pollCount >= maxPollCount) {
            finishTranscribing('timeout')
            stopPolling(pollInterval)
          }
        } catch {
          if (pollCount >= maxPollCount) {
            finishTranscribing('timeout')
            stopPolling(pollInterval)
          }
        }
      }, 10000)

      transcribePollIntervalsRef.current.add(pollInterval)
    } catch {
      finishTranscribing('error')
    }
  }

  // ── Drag and drop ─────────────────────────────────────────────────────────

  let handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    let src = result.source.index
    let dst = result.destination.index
    if (src === dst) return
    let contentCount = localUnifiedContent.length
    if (src >= contentCount || dst >= contentCount) return

    let moved = localUnifiedContent[src]
    let without = localUnifiedContent.filter((_, i) => i !== src)
    let reordered = [...without.slice(0, dst), moved, ...without.slice(dst)]
    setLocalUnifiedContent(reordered)

    let steps = reordered
      .filter(
        (
          i,
        ): i is
          | { kind: 'showNotes'; data: PipelineShowNotes }
          | { kind: 'video'; data: PipelineVideo } =>
          i.kind === 'showNotes' || i.kind === 'video',
      )
      .map((i) => ({
        type: (i.kind === 'video' ? 'Video' : 'ShowNotes') as
          'Video' | 'ShowNotes',
        videoID: i.kind === 'video' ? i.data.id : null,
        showNotesID: i.kind === 'showNotes' ? i.data.id : null,
      }))

    dispatch(updateCourseV2ContentSteps({ courseId: courseID, steps }))
  }

  // ── Show notes actions ────────────────────────────────────────────────────

  let handleAddShowNotes = () => {
    // eslint-disable-next-line fp/no-mutation
    expectedSnCountRef.current = showNotesList.length + 1
    dispatch(
      createCourseShowNotes({
        courseId: courseID,
        payload: { name: addSnName, description: addSnDescription || null },
      }),
    )
    setAddSnFormOpen(false)
    setAddSnName('')
    setAddSnDescription('')
  }

  let handleMigrateSuccess = () => {
    // eslint-disable-next-line fp/no-mutation
    expectedSnCountRef.current = showNotesList.length + 1
  }

  useEffect(() => {
    if (
      expectedSnCountRef.current !== null &&
      showNotesList.length >= expectedSnCountRef.current
    ) {
      setEditingShowNotesID(showNotesList[showNotesList.length - 1].id)
      // eslint-disable-next-line fp/no-mutation
      expectedSnCountRef.current = null
    }
  }, [showNotesList])

  let handleDeleteShowNotes = () => {
    if (!deletingShowNotesID) return
    dispatch(
      deleteCourseShowNotes({
        courseId: courseID,
        showNotesId: deletingShowNotesID,
      }),
    )
  }

  useEffect(() => {
    if (!deletingShowNotesID) return

    if (deleteShowNotesStatus === 'fulfilled') {
      setDeletingShowNotesID(null)
    } else if (deleteShowNotesStatus === 'rejected') {
      setDeletingShowNotesID(null)
      showError(
        deleteShowNotesError ??
          'Failed to delete show notes. Please try again.',
      )
    }
  }, [
    deleteShowNotesStatus,
    deletingShowNotesID,
    deleteShowNotesError,
    showError,
  ])

  let openEditShowNotesInfo = (sn: PipelineShowNotes) => {
    setEditingShowNotesInfo(sn)
    setEditSnInfoName(sn.name)
    setEditSnInfoDescription(sn.description ?? '')
  }

  let handleSaveShowNotesInfo = () => {
    if (!editingShowNotesInfo) return
    dispatch(
      updateCourseShowNotes({
        showNotesID: editingShowNotesInfo.id,
        courseID,
        payload: {
          name: editSnInfoName,
          description: editSnInfoDescription || null,
        },
      }),
    )
    setEditingShowNotesInfo(null)
  }

  // ── Video actions ─────────────────────────────────────────────────────────

  let handleAddVideo = async () => {
    if (!videoFile || !videoName.trim()) return
    setVideoUploadError(null)
    setVideoUploadProgress(0)
    try {
      let key = await uploadVideoToS3(videoFile, setVideoUploadProgress)
      dispatch(
        createCourseVideo({
          courseId: courseID,
          payload: { name: videoName, description: videoDescription, key },
        }),
      )
      setAddVideoOpen(false)
      setVideoName('')
      setVideoDescription('')
      setVideoFile(null)
      setVideoUploadProgress(null)
    } catch {
      setVideoUploadError('Upload failed. Please try again.')
      setVideoUploadProgress(null)
    }
  }

  let handleDeleteVideo = () => {
    if (!deletingVideoID) return
    dispatch(
      deleteCourseVideo({ videoId: deletingVideoID, courseId: courseID }),
    )
  }

  useEffect(() => {
    if (deleteVideoStatus === 'fulfilled') {
      setDeletingVideoID(null)
    } else if (deleteVideoStatus === 'rejected') {
      setDeletingVideoID(null)
      showError(deleteVideoError ?? 'Failed to delete video. Please try again.')
    }
  }, [deleteVideoStatus])

  let openEditVideoInfo = (v: PipelineVideo) => {
    setEditingVideoInfo(v)
    setEditVideoInfoName(v.name)
    setEditVideoInfoDescription(v.description)
  }

  let handleSaveVideoInfo = () => {
    if (!editingVideoInfo) return
    dispatch(
      updateCourseVideo({
        videoId: editingVideoInfo.id,
        courseId: courseID,
        payload: {
          name: editVideoInfoName,
          description: editVideoInfoDescription,
        },
      }),
    )
    setEditingVideoInfo(null)
  }

  // ── Migratable content actions ────────────────────────────────────────────

  let handleMigrateVideo = (videoID: string) => {
    dispatch(migrateCourseV2Video({ courseID, videoID }))
  }

  let handleMigrateShowNote = (showNotesID: string) => {
    dispatch(migrateCourseV2ShowNote({ courseID, showNotesID }))
  }

  // ── Open add dialogs, gating on approved confirmation ────────────────────

  let openAddDialog = (kind: 'showNotes' | 'video') => {
    if (complianceStatus === 'approved') {
      setPendingAddKind(kind)
      setApprovedConfirmOpen(true)
    } else if (kind === 'showNotes') {
      setAddSnFormOpen(true)
    } else {
      setAddVideoOpen(true)
    }
  }

  let handleApprovedConfirm = () => {
    setApprovedConfirmOpen(false)
    if (pendingAddKind === 'showNotes') setAddSnFormOpen(true)
    else if (pendingAddKind === 'video') setAddVideoOpen(true)
    setPendingAddKind(null)
  }

  // ── Derived ───────────────────────────────────────────────────────────────

  let editingShowNotes = showNotesList.find(
    (sn) => sn.id === editingShowNotesID,
  )

  let isLoading =
    showNotesStatus === 'pending' ||
    showNotesStatus === 'uninitialized' ||
    videosStatus === 'pending' ||
    videosStatus === 'uninitialized' ||
    legacyTextContentStatus === 'pending' ||
    legacyTextContentStatus === 'uninitialized'

  return (
    <Box>
      {/* Compliance status badge */}
      {complianceStatus && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
          }}
        >
          {complianceStatus === 'approved' && (
            <LockIcon fontSize="small" color="success" />
          )}
          <Chip
            size="small"
            label={complianceChipLabel(complianceStatus)}
            color={complianceChipColor(complianceStatus)}
          />
        </Box>
      )}

      {/* Compliance feedback alert */}
      {(complianceStatus === 'rejected' || complianceStatus === 'unapproved') &&
        complianceFeedback && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {complianceFeedback}
          </Alert>
        )}

      {/* Migratable items — always visible, independent of the course-content fetch */}
      {migratableItems.length > 0 && (
        <Card sx={{ bgcolor: 'background.paperDark', mb: 2 }}>
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
              Legacy Content Migration
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                This course has legacy content that needs to be migrated. Videos
                and Show Notes can be automatically migrated to course content.
              </Typography>
            </Alert>
            <Box
              component="ul"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 0,
                m: 0,
                listStyle: 'none',
              }}
            >
              {migratableItems.map((item) => {
                if (item.kind === 'migratableVideo') {
                  let mv = item.data
                  return (
                    <Box
                      component="li"
                      key={`mv-${mv.id}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        minWidth: 0,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flex: 1,
                          minWidth: 0,
                          gap: 1.5,
                        }}
                      >
                        <VideoIcon
                          fontSize="small"
                          sx={{ color: 'primary.main' }}
                          aria-hidden
                        />
                        <Typography variant="body2" noWrap>
                          {mv.name}
                        </Typography>
                      </Box>
                      <Button
                        color="warning"
                        size="large"
                        variant="outlined"
                        onClick={() => handleMigrateVideo(mv.id)}
                        aria-label={`Migrate Video: ${mv.name}`}
                        sx={{ ml: 2, minWidth: 160 }}
                      >
                        Migrate Video
                      </Button>
                    </Box>
                  )
                }
                let msn = item.data as CourseMigratableShowNote
                return (
                  <Box
                    component="li"
                    key={`msn-${msn.id}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      minWidth: 0,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                        minWidth: 0,
                        gap: 1.5,
                      }}
                    >
                      <ShowNotesIcon
                        fontSize="small"
                        sx={{ color: 'success.main' }}
                        aria-hidden
                      />
                      <Typography variant="body2" noWrap>
                        {msn.name}
                      </Typography>
                    </Box>
                    <Button
                      color="warning"
                      size="large"
                      variant="outlined"
                      onClick={() => handleMigrateShowNote(msn.id)}
                      aria-label={`Migrate Show Notes: ${msn.name}`}
                      sx={{ ml: 2, minWidth: 160 }}
                    >
                      Migrate Show Notes
                    </Button>
                  </Box>
                )
              })}
            </Box>
          </Box>
        </Card>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!isLoading &&
        unifiedList.length === 0 &&
        migratableItems.length === 0 && (
          <Box
            sx={{
              py: 6,
              textAlign: 'center',
              bgcolor: 'background.paperDark',
              borderRadius: 1,
            }}
          >
            <VideoIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" variant="body2">
              No content yet. Add videos or show notes below.
            </Typography>
          </Box>
        )}

      {!isLoading && unifiedList.length > 0 && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="course-content">
            {(provided) => (
              <Box
                {...provided.droppableProps}
                ref={provided.innerRef}
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
              >
                {unifiedList.map((item, index) => {
                  if (item.kind === 'showNotes') {
                    let sn = item.data
                    return (
                      <Draggable key={sn.id} draggableId={sn.id} index={index}>
                        {(draggableProvided, snapshot) => (
                          <Card
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            variant="outlined"
                            sx={{
                              p: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              minWidth: 0,
                              bgcolor: snapshot.isDragging
                                ? 'action.selected'
                                : 'background.paperDark',
                              transition: 'background-color 0.15s',
                            }}
                          >
                            <Box
                              {...draggableProvided.dragHandleProps}
                              sx={{
                                display: 'flex',
                                color: 'text.disabled',
                                cursor: 'grab',
                              }}
                              aria-label="Drag to reorder"
                            >
                              <DragAndDropIcon fontSize="small" />
                            </Box>
                            <ShowNotesIcon
                              fontSize="small"
                              sx={{ color: 'success.main' }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ fontWeight: 'medium' }}
                              >
                                {sn.name}
                              </Typography>
                              {sn.description && (
                                <Typography
                                  color="text.secondary"
                                  variant="caption"
                                  noWrap
                                  sx={{ display: 'block' }}
                                >
                                  {sn.description}
                                </Typography>
                              )}
                            </Box>
                            <Tooltip title="Preview show notes">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => setPreviewingShowNotes(sn)}
                                aria-label={`Preview show notes: ${sn.name}`}
                              >
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit title & description">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => openEditShowNotesInfo(sn)}
                                aria-label={`Edit title & description: ${sn.name}`}
                              >
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit show notes">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => setEditingShowNotesID(sn.id)}
                                aria-label={`Edit show notes content: ${sn.name}`}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete show notes">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => setDeletingShowNotesID(sn.id)}
                                aria-label={`Delete show notes: ${sn.name}`}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Card>
                        )}
                      </Draggable>
                    )
                  }

                  if (item.kind === 'video') {
                    let v = item.data
                    return (
                      <Draggable key={v.id} draggableId={v.id} index={index}>
                        {(draggableProvided, snapshot) => (
                          <Card
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            variant="outlined"
                            sx={{
                              p: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              minWidth: 0,
                              bgcolor: snapshot.isDragging
                                ? 'action.selected'
                                : 'background.paperDark',
                              transition: 'background-color 0.15s',
                            }}
                          >
                            <Box
                              {...draggableProvided.dragHandleProps}
                              sx={{
                                display: 'flex',
                                color: 'text.disabled',
                                cursor: 'grab',
                              }}
                              aria-label="Drag to reorder"
                            >
                              <DragAndDropIcon fontSize="small" />
                            </Box>
                            <VideoIcon
                              fontSize="small"
                              sx={{ color: 'primary.main' }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ fontWeight: 'medium' }}
                              >
                                {v.name}
                              </Typography>
                              {v.description && (
                                <Typography
                                  color="text.secondary"
                                  variant="caption"
                                  noWrap
                                  sx={{ display: 'block' }}
                                >
                                  {v.description}
                                </Typography>
                              )}
                            </Box>
                            <Tooltip title="Preview video">
                              <IconButton
                                size="small"
                                onClick={() => setPreviewingVideo(v)}
                                aria-label={`Preview video: ${v.name}`}
                              >
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download video">
                              <IconButton
                                size="small"
                                onClick={() => setDownloadingVideo(v)}
                                aria-label={`Download video: ${v.name}`}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit title & description">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => openEditVideoInfo(v)}
                                aria-label={`Edit title & description: ${v.name}`}
                              >
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                transcriptAvailability[v.id]
                                  ? 'Edit transcript'
                                  : 'Transcript not available yet'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setTranscriptModalVideoId(v.id)
                                  }
                                  disabled={
                                    !transcriptAvailability[v.id] ||
                                    transcribing[v.id]
                                  }
                                  aria-label={`Edit transcript: ${v.name}`}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={
                                transcribing[v.id]
                                  ? 'Transcribing…'
                                  : transcriptAvailability[v.id]
                                    ? 'Re-transcribe'
                                    : 'Transcribe'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color={
                                    transcriptAvailability[v.id]
                                      ? 'error'
                                      : 'primary'
                                  }
                                  onClick={() =>
                                    setTranscribeConfirmVideoId(v.id)
                                  }
                                  disabled={transcribing[v.id]}
                                  aria-label={
                                    transcribing[v.id]
                                      ? `Transcribing: ${v.name}`
                                      : transcriptAvailability[v.id]
                                        ? `Re-transcribe: ${v.name}`
                                        : `Transcribe: ${v.name}`
                                  }
                                >
                                  {transcribing[v.id] ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <TranscribeIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete video">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => setDeletingVideoID(v.id)}
                                aria-label={`Delete video: ${v.name}`}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Card>
                        )}
                      </Draggable>
                    )
                  }

                  if (item.kind !== 'legacy') return null
                  let tc = item.data
                  return (
                    <Draggable
                      key={`legacy-${tc.id}`}
                      draggableId={`legacy-${tc.id}`}
                      index={index}
                      isDragDisabled
                    >
                      {(draggableProvided) => (
                        <Card
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          variant="outlined"
                          sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            minWidth: 0,
                            bgcolor: 'background.paperDark',
                            borderColor: 'warning.main',
                          }}
                        >
                          <Box sx={{ display: 'flex', color: 'text.disabled' }}>
                            <DragAndDropIcon fontSize="small" />
                          </Box>
                          <ShowNotesIcon
                            fontSize="small"
                            sx={{ color: 'warning.main' }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ fontWeight: 'medium' }}
                            >
                              {tc.name}
                            </Typography>
                            <Typography color="warning.main" variant="caption">
                              Legacy — migrate to use
                            </Typography>
                          </Box>
                          <Button
                            color="warning"
                            size="small"
                            variant="outlined"
                            onClick={() => setMigratingTextContent(tc)}
                          >
                            Migrate
                          </Button>
                        </Card>
                      )}
                    </Draggable>
                  )
                })}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Action buttons */}
      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => openAddDialog('showNotes')}
        >
          Add Show Notes
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => openAddDialog('video')}
        >
          Add Video
        </Button>
      </Box>

      {/* Approved field confirmation */}
      <Dialog
        open={approvedConfirmOpen}
        onClose={() => setApprovedConfirmOpen(false)}
        fullWidth
        slotProps={{ paper: modalPaperProps }}
        maxWidth="xs"
      >
        <DialogTitle>Content Field Approved</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This content has been approved. Adding or removing items will
            require re-approval from compliance.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovedConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleApprovedConfirm}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add show notes dialog */}
      <Dialog
        open={addSnFormOpen}
        onClose={() => setAddSnFormOpen(false)}
        fullWidth
        slotProps={{ paper: modalPaperProps }}
        maxWidth="xs"
      >
        <DialogTitle>Add Show Notes</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a new Show Notes entry for this course.
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={addSnName}
              onChange={(e) => setAddSnName(e.target.value)}
              size="small"
              fullWidth
              required
              autoFocus
              sx={modalInputSx}
            />
            <TextField
              label="Description (optional)"
              value={addSnDescription}
              onChange={(e) => setAddSnDescription(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={2}
              sx={modalInputSx}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddSnFormOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddShowNotes}
            disabled={!addSnName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add video dialog */}
      <Dialog
        open={addVideoOpen}
        onClose={() => {
          if (videoUploadProgress !== null) return
          setAddVideoOpen(false)
          setVideoName('')
          setVideoDescription('')
          setVideoFile(null)
          setVideoUploadError(null)
        }}
        fullWidth
        slotProps={{ paper: modalPaperProps }}
        maxWidth="sm"
      >
        <DialogTitle>Add Video</DialogTitle>
        <DialogContent>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}
          >
            <TextField
              label="Name"
              value={videoName}
              onChange={(e) => setVideoName(e.target.value)}
              size="small"
              fullWidth
              required
              autoFocus
              sx={modalInputSx}
            />
            <TextField
              label="Description (optional)"
              value={videoDescription}
              onChange={(e) => setVideoDescription(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={2}
              sx={modalInputSx}
            />
            <Button
              variant="outlined"
              component="label"
              size="small"
              disabled={videoUploadProgress !== null}
            >
              {videoFile ? videoFile.name : 'Choose MP4 file…'}
              <input
                type="file"
                accept="video/mp4"
                hidden
                onChange={(e) => {
                  let f = e.target.files?.[0]
                  if (f) setVideoFile(f)
                }}
              />
            </Button>
            {videoUploadProgress !== null && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Uploading… {videoUploadProgress}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={videoUploadProgress}
                  sx={{ mt: 0.5 }}
                />
              </Box>
            )}
            {videoUploadError && (
              <Alert severity="error">{videoUploadError}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddVideoOpen(false)
              setVideoName('')
              setVideoDescription('')
              setVideoFile(null)
              setVideoUploadError(null)
            }}
            disabled={videoUploadProgress !== null}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddVideo}
            disabled={
              !videoName.trim() || !videoFile || videoUploadProgress !== null
            }
          >
            {videoUploadProgress !== null ? 'Uploading…' : 'Upload & Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit show notes info dialog */}
      <Dialog
        open={!!editingShowNotesInfo}
        onClose={() => setEditingShowNotesInfo(null)}
        fullWidth
        slotProps={{ paper: modalPaperProps }}
        maxWidth="xs"
      >
        <DialogTitle>Edit Show Notes Info</DialogTitle>
        <DialogContent>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}
          >
            <TextField
              label="Name"
              value={editSnInfoName}
              onChange={(e) => setEditSnInfoName(e.target.value)}
              size="small"
              fullWidth
              required
              autoFocus
              sx={modalInputSx}
            />
            <TextField
              label="Description (optional)"
              value={editSnInfoDescription}
              onChange={(e) => setEditSnInfoDescription(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={2}
              sx={modalInputSx}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingShowNotesInfo(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveShowNotesInfo}
            disabled={!editSnInfoName.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit video info dialog */}
      <Dialog
        open={!!editingVideoInfo}
        onClose={() => setEditingVideoInfo(null)}
        fullWidth
        slotProps={{ paper: modalPaperProps }}
        maxWidth="xs"
      >
        <DialogTitle>Edit Video Info</DialogTitle>
        <DialogContent>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}
          >
            <TextField
              label="Name"
              value={editVideoInfoName}
              onChange={(e) => setEditVideoInfoName(e.target.value)}
              size="small"
              fullWidth
              required
              autoFocus
              sx={modalInputSx}
            />
            <TextField
              label="Description (optional)"
              value={editVideoInfoDescription}
              onChange={(e) => setEditVideoInfoDescription(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={2}
              sx={modalInputSx}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingVideoInfo(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveVideoInfo}
            disabled={!editVideoInfoName.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete show notes confirmation */}
      <Dialog
        open={!!deletingShowNotesID}
        onClose={() => setDeletingShowNotesID(null)}
        fullWidth
        slotProps={{ paper: modalPaperProps }}
        maxWidth="xs"
      >
        <DialogTitle>Delete Show Notes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this show notes entry? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeletingShowNotesID(null)}
            disabled={isDeletingShowNotes}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteShowNotes}
            loading={isDeletingShowNotes}
            disabled={isDeletingShowNotes}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete video confirmation */}
      <Dialog
        open={!!deletingVideoID}
        onClose={() => setDeletingVideoID(null)}
        fullWidth
        slotProps={{ paper: modalPaperProps }}
        maxWidth="xs"
      >
        <DialogTitle>Delete Video</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this video? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeletingVideoID(null)}
            disabled={isDeletingVideo}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteVideo}
            loading={isDeletingVideo}
            disabled={isDeletingVideo}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Text content migration modal */}
      {migratingTextContent && (
        <MigrateTextContentModal
          isOpen={!!migratingTextContent}
          onClose={() => setMigratingTextContent(null)}
          courseID={courseID}
          textContent={migratingTextContent}
          onMigrateSuccess={handleMigrateSuccess}
        />
      )}

      {/* Show notes preview modal */}
      {previewingShowNotes && (
        <CourseShowNotesPreviewModal
          isOpen={!!previewingShowNotes}
          onClose={() => setPreviewingShowNotes(null)}
          showNotesID={previewingShowNotes.id}
          showNotesName={previewingShowNotes.name}
          showNotesDescription={previewingShowNotes.description}
        />
      )}

      {/* Video preview modal */}
      {previewingVideo && (
        <VideoPreviewModal
          isOpen={!!previewingVideo}
          onClose={() => setPreviewingVideo(null)}
          video={previewingVideo}
        />
      )}

      {/* Video download modal */}
      {downloadingVideo && (
        <DownloadVideoModal
          isOpen={!!downloadingVideo}
          onClose={() => setDownloadingVideo(null)}
          videoId={downloadingVideo.id}
          videoName={downloadingVideo.name}
        />
      )}

      {/* Show notes block editor modal */}
      {editingShowNotes && (
        <CourseShowNotesModal
          isOpen={!!editingShowNotes}
          onClose={() => setEditingShowNotesID(null)}
          showNotesID={editingShowNotes.id}
          showNotesName={editingShowNotes.name}
        />
      )}

      {/* Transcribe confirmation dialog */}
      <Dialog
        open={!!transcribeConfirmVideoId}
        onClose={() => setTranscribeConfirmVideoId(null)}
        fullWidth
        slotProps={{ paper: modalPaperProps }}
        maxWidth="xs"
      >
        <DialogTitle>
          {transcribeConfirmVideoId &&
          transcriptAvailability[transcribeConfirmVideoId]
            ? 'Re-run Transcription?'
            : 'Start Transcription?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {transcribeConfirmVideoId &&
            transcriptAvailability[transcribeConfirmVideoId]
              ? 'This will replace the current transcript once the new job completes. Freshly uploaded videos are transcribed automatically within a few minutes — only re-run this if the transcript is out of date or the original job failed.'
              : "This video doesn't have a transcript yet. Freshly uploaded videos are transcribed automatically within a few minutes, but videos migrated from a legacy course won't be — trigger it manually here to generate one."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTranscribeConfirmVideoId(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (transcribeConfirmVideoId) {
                handleTranscribe(transcribeConfirmVideoId)
              }
              setTranscribeConfirmVideoId(null)
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transcript editor modal */}
      {transcriptModalVideoId && (
        <PipelineTranscriptEditorModal
          open={!!transcriptModalVideoId}
          onClose={() => setTranscriptModalVideoId(null)}
          videoId={transcriptModalVideoId}
          courseId={courseID}
        />
      )}

      {NotificationComponent}
    </Box>
  )
}
